import React, { forwardRef, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useField, useForm } from "react-final-form"; // Import useForm to access form methods
import {
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
} from "@material-ui/core";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import ClearIcon from "@material-ui/icons/Clear";
import { required } from "react-admin";

interface ICustomMonthPickerInput {
  name: string;
  yearFieldName?: string;
  monthFieldName?: string;
  label?: string;
  disableValue?: boolean;
  showClearButton?: boolean;
  placeholderValue?: string;
  year?: number; // Adding props for year initialization
  month?: number; // Adding props for month initialization
  excludeDates?: Date[];
  [key: string]: any;
}

const useStyles = makeStyles({
  root: {
    marginTop: "8px",
    marginBottom: "8px",
    width: "100%",
    backgroundColor: "#FFFFFF",
  },
  override: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": {
        borderColor: "#EAEAEA !important",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA !important",
      },
    },
  },
  picker: {
    "& .react-datepicker-wrapper": {
      width: "100%",
    },
    "& .react-datepicker-popper": {
      zIndex: 10,
    },
  },
});

const CustomMonthPickerInput = ({
  name,
  label,
  disableValue,
  placeholderValue,
  year, // Props to receive the initial year
  month, // Props to receive the initial month
  showClearButton,
  isRequired,
  minMonth,
  minYear,
  maxMonth,
  maxYear,
  yearFieldName = "year",
  monthFieldName = "month",
  excludeDates,
}: ICustomMonthPickerInput) => {
  const classes = useStyles();
  const form = useForm(); // Access form methods to update Month and Year

  const {
    input: { onChange, value },
  } = useField(name, {
    validate: isRequired ? required() : undefined,
  });

  // Convert the month and year to a date object for initialization
  const initialDate = month && year ? new Date(year, month - 1, 1) : new Date();

  const [date, setDate] = useState(initialDate);

  // Compute minDate and maxDate from minMonth/year and maxMonth/year
  const computedMinDate =
    minMonth && minYear
      ? new Date(minYear, minMonth - 1, 1)
      : null;

  const computedMaxDate =
    maxMonth && maxYear
      ? new Date(maxYear, maxMonth - 1, 1)
      : null;

  // --- START CSS WORKAROUND ---
  // showMonthYearPicker correctly prevents selecting `excludeDates`,
  // but it fails to apply the disabled CSS class (.react-datepicker__month-text--disabled).
  // The code below manually injects CSS to visually disable them.

  // Track the year currently displayed in the calendar popup
  const [viewYear, setViewYear] = useState(
    date ? date.getFullYear() : new Date().getFullYear()
  );

  // Create a random unique ID for this instance so our injected CSS doesn't accidentally
  // style other datepickers on the same page
  const [instanceId] = useState(
    () => `picker-${Math.random().toString(36).substr(2, 9)}`
  );

  // Sync the viewed year if the date is updated outside of this component
  useEffect(() => {
    if (date) {
      setViewYear(date.getFullYear());
    }
  }, [date]);

  // Update viewYear when navigates using previous/next year buttons
  const handleYearChange = (newDate: Date) => {
    setViewYear(newDate.getFullYear());
  };

  // Reset the viewYear when the calendar popup opens.
  // Otherwise, if a user navigated to a different year and closed the popup,
  // reopening it would cause a mismatch between the calendar UI and our state.
  const handleCalendarOpen = () => {
    setViewYear(date ? date.getFullYear() : new Date().getFullYear());
  };

  // Get list of disabled months, but only for the currently viewed year
  const disabledMonths = Array.from(
    new Set(
      (excludeDates || [])
        .filter((d) => d.getFullYear() === viewYear)
        .map((d) => d.getMonth())
    )
  );

  // Build the CSS to gray out the disabled months.
  // Since the datepicker renders months in a strict 4x3 grid (4 rows, 3 columns),
  // we can calculate the exact nth-child selector to target.
  // Adding pointer-events: none ensures clicks are fully ignored.
  const disabledMonthsCss = disabledMonths
    .map((m) => {
      const wrapperIndex = Math.floor(m / 3) + 1;
      const childIndex = (m % 3) + 1;
      return `
        .${instanceId} .react-datepicker__month-wrapper:nth-child(${wrapperIndex}) > .react-datepicker__month-text:nth-child(${childIndex}) {
          color: #ccc !important;
          cursor: default !important;
          background-color: transparent !important;
          pointer-events: none !important;
        }
      `;
    })
    .join("\n");
  // --- END CSS WORKAROUND ---

  // Use useEffect to update date state if month or year changes externally
  useEffect(() => {
    if (month && year) {
      const newDate = new Date(year, month - 1, 1);
      setDate(newDate);
      handleDateChange(newDate);
    }
  }, [month, year]);

  // Function to check if date is within the allowed range
  const isDateInRange = (date: Date) => {
    // If a minimum date is defined and the selected date is before it, it's invalid
    if (computedMinDate && date < computedMinDate) return false;

    // If a maximum date is defined and the selected date is after it, it's invalid
    if (computedMaxDate && date > computedMaxDate) return false;

    // Date is within range
    return true;
  };

  const handleDateChange = (selectedDate: Date) => {
    // Set to the first day of the selected month to avoid "Invalid date"
    const correctedDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      1,
    );

    // If the corrected date is out of range, ignore the change
    if (!isDateInRange(correctedDate)) {
      return;
    }

    setDate(correctedDate);
    onChange(moment(correctedDate).format("MM/yyyy"));

    // Extract the month and year and update the form fields
    const month = correctedDate.getMonth() + 1; // +1 because getMonth() returns 0-11
    const year = correctedDate.getFullYear();

    // Update form fields for Month and Year
    form.change(monthFieldName, month);
    form.change(yearFieldName, year);
  };

  const handleClearDate = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the popup from opening

    setDate(null); // Reset date to null
    onChange(null); // Clear form field value
    form.change(monthFieldName, null); // Clear month field
    form.change(yearFieldName, null); // Clear year field
  };

  const CustomInput = forwardRef(({ onClick }: any, ref: any) => (
    <div className={classes.override}>
      <TextField
        className={classes.root}
        variant="filled"
        size="small"
        label={label}
        value={
          placeholderValue
            ? placeholderValue
            : value
            ? moment(value, "MM/yyyy").format("MM/yyyy")
            : ""
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton edge="end" onClick={onClick}>
                <CalendarTodayIcon />
              </IconButton>
              {value && showClearButton && (
                <IconButton edge="end" onClick={handleClearDate}>
                  <ClearIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
        onClick={onClick}
        ref={ref}
        disabled={disableValue}
      />
    </div>
  ));

  return (
    <div className={classes.picker}>
      <style>{disabledMonthsCss}</style>
      <DatePicker
        selected={date}
        calendarClassName={instanceId}
        onChange={handleDateChange}
        onYearChange={handleYearChange}
        onCalendarOpen={handleCalendarOpen}
        showMonthYearPicker
        dateFormat="MM/yyyy"
        customInput={<CustomInput />}
        disabled={disableValue}
        minDate={computedMinDate}
        maxDate={computedMaxDate}
        excludeDates={excludeDates}
        todayButton="Today"
      />
    </div>
  );
};

export default CustomMonthPickerInput;
